import { DirectUpload } from "@rails/activestorage"
import * as DateFns from "date-fns"
import { filetypeinfo } from "magic-bytes.js"
import { type uuid } from "../../../../../../../model/types"
import api, { type ApiFileInfo } from "../../../../../../../services/api"

type StateWithUrl = {
  type: "with-url"
  filename: string
  signedUrl: URL
  expiresAt: Date
}
type StateWithBlob = {
  type: "with-blob"
  filename: string
  blob?: Blob
  saving: boolean
  error?: string
}

export type ImageState =
  | { type: "needs-check" } // we have a uuid, but don't know if this is an empty block or an uploaded image
  | { type: "fetching" } // we're checking
  | { type: "empty"; error?: string } // we checked, and it's an empty block
  | StateWithUrl // we checked, and it's an uploaded image
  | StateWithBlob // we have a blob, and we know if it's being saved currently

export default class ImageStore {
  states: Map<uuid, ImageState>
  subscriptions: Map<uuid, Set<() => void>>

  constructor() {
    this.states = new Map<uuid, ImageState>()
    this.subscriptions = new Map<uuid, Set<() => void>>()
  }

  getImageState(uuid: uuid): ImageState {
    const existingState = this.states.get(uuid)

    if (
      existingState &&
      (existingState as StateWithUrl).signedUrl &&
      (existingState as StateWithUrl).expiresAt < new Date()
    ) {
      const newState: ImageState = { type: "needs-check" }
      // console.log("image url expired, resetting to needs-check")
      this.setImageState(uuid, newState)
      return newState
    }

    if (existingState) return existingState

    const newState: ImageState = this.initState()
    this.states.set(uuid, newState)
    return newState
  }

  setImageState(uuid: uuid, newState: ImageState): void {
    this.states.set(uuid, newState)
    this.subscriptions.get(uuid)?.forEach((onStoreChange) => onStoreChange())
  }

  initState(): ImageState {
    return { type: "needs-check" }
  }

  subscribeTo(uuid: uuid): (onStoreChange: () => void) => () => void {
    if (!this.subscriptions.has(uuid)) this.subscriptions.set(uuid, new Set())
    return (onStoreChange) => {
      this.subscriptions.get(uuid)?.add(onStoreChange)
      return () => this.subscriptions.get(uuid)?.delete(onStoreChange)
    }
  }

  imageStateFetcherFor(uuid: uuid): () => ImageState {
    return () => this.getImageState(uuid)
  }

  gotFile(uuid: uuid, blob: Blob, file: File): void {
    this.setImageState(uuid, {
      type: "with-blob",
      filename: file.name,
      blob,
      saving: true,
    })

    // Create a direct upload to S3
    const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads")

    upload.create((error, blob) => {
      if (error) {
        console.error("uploadFile error", error)
        const currentState = this.getImageState(uuid) as StateWithBlob
        this.setImageState(uuid, {
          type: "with-blob",
          filename: file.name,
          blob: currentState.blob,
          saving: false,
          error: "There was an error saving this file.",
        })
        return
      }

      // Associate the blob with our model
      api()
        .post("/api/files/attach", {
          uuid: uuid,
          signed_blob_id: blob.signed_id,
        })
        .then(() => {
          const state = this.getImageState(uuid) as StateWithBlob
          this.setImageState(uuid, {
            type: "with-blob",
            filename: file.name,
            blob: state.blob,
            saving: false,
          })
        })
        .catch((error) => {
          console.error("attach blob error", error)
          const currentState = this.getImageState(uuid) as StateWithBlob
          this.setImageState(uuid, {
            type: "with-blob",
            filename: file.name,
            blob: currentState.blob,
            saving: false,
            error: "There was an error saving this file.",
          })
        })
    })
  }

  parseImageFile(
    file: File,
    callback: (result: {
      error: string | null
      blob: Blob | null
      name: string
      type: string
      sizeMb: number
    }) => void
  ): void {
    const sizeMb = file.size / 1024 / 1024
    const type = file.type
    const name = file.name

    if (type !== "image/jpeg" && type !== "image/png" && type !== "image/gif")
      return callback({
        type,
        sizeMb,
        name,
        error: "Not an image file",
        blob: null,
      })
    if (sizeMb > 8)
      return callback({
        type,
        sizeMb,
        name,
        error: "Image file too large (under 8 Mb please)",
        blob: null,
      })

    const fileReader = new FileReader()
    fileReader.onloadend = (f) => {
      if (!f.target) return
      const arrayBuffer = f.target.result as ArrayBuffer
      const bytes = new Uint8Array(arrayBuffer)
      const detectedFileTypes = filetypeinfo(bytes).map((ft) => ft.mime)
      if (
        !detectedFileTypes.includes("image/jpeg") &&
        !detectedFileTypes.includes("image/png") &&
        !detectedFileTypes.includes("image/gif")
      )
        return callback({
          type,
          sizeMb,
          name,
          error: "Not an image file",
          blob: null,
        })
      const blob = new Blob([arrayBuffer])
      callback({ blob: blob, type, sizeMb, name, error: null })
    }
    fileReader.readAsArrayBuffer(file)
  }

  fetchImageSignedUrlsFromUuids(imageUuids: uuid[]): void {
    if (imageUuids.length == 0) return

    const neededImageUuids = imageUuids.filter((uuid) => {
      const state = this.getImageState(uuid)
      return (
        state.type == "needs-check" || (state.type == "with-url" && state.expiresAt < new Date())
      )
    }) as uuid[]

    if (!neededImageUuids.length) return

    neededImageUuids.forEach((uuid) => {
      this.setImageState(uuid, { type: "fetching" })
    })

    api()
      .post("/api/files/urls", { uuids: neededImageUuids })
      .then(({ data }) => {
        const { files }: { files: ApiFileInfo[] } = data

        files.forEach((file: ApiFileInfo) => {
          this.setImageState(file.uuid, {
            type: "with-url",
            filename: file.filename,
            signedUrl: new URL(file.url),
            expiresAt: DateFns.parseISO(file.expires_at),
          })
        })

        const foundUuids = files.map((file: ApiFileInfo) => file.uuid)
        neededImageUuids.forEach((uuid) => {
          if (!foundUuids.includes(uuid)) {
            this.setImageState(uuid, { type: "empty" })
          }
        })
      })
  }
}
